export const dashboardPalette = {
  primary: '#3559c7',
  secondary: '#8ea5ea',
  accent: '#3f9a90',
  rose: '#c65d73',
  slate: '#1f2a44',
  muted: '#6b7a90',
  grid: 'rgba(107, 122, 144, 0.18)',
};

const baseChartOptions = {
  chart: {
    toolbar: {
      show: false,
    },
    zoom: {
      enabled: false,
    },
    fontFamily: 'inherit',
    foreColor: dashboardPalette.muted,
    animations: {
      enabled: true,
      easing: 'easeinout',
      speed: 450,
    },
  },
  grid: {
    borderColor: dashboardPalette.grid,
    strokeDashArray: 4,
    padding: {
      left: 10,
      right: 12,
      top: 8,
      bottom: 0,
    },
  },
  dataLabels: {
    enabled: true,
    style: {
      colors: [dashboardPalette.slate],
      fontWeight: 600,
      fontSize: '11px',
    },
    offsetX: 6,
    background: {
      enabled: false,
    },
  },
  legend: {
    position: 'top',
    horizontalAlign: 'left',
    fontWeight: 600,
    labels: {
      colors: dashboardPalette.slate,
    },
    markers: {
      radius: 999,
    },
  },
  tooltip: {
    theme: 'light',
  },
  states: {
    hover: {
      filter: {
        type: 'lighten',
        value: 0.08,
      },
    },
  },
};

export const buildHorizontalBarOptions = ({
  categories,
  xTitle,
  color = dashboardPalette.primary,
  tooltipFormatter,
  barHeight = '52%',
  height = 300,
  borderRadius = 7,
  showDataLabels = true,
}) => ({
  ...baseChartOptions,
  chart: {
    ...baseChartOptions.chart,
    type: 'bar',
    height,
  },
  plotOptions: {
    bar: {
      horizontal: true,
      barHeight,
      borderRadius,
      distributed: false,
    },
  },
  xaxis: {
    title: {
      text: xTitle,
      style: {
        color: dashboardPalette.muted,
        fontWeight: 600,
      },
    },
    categories,
    labels: {
      style: {
        colors: dashboardPalette.muted,
      },
    },
  },
  yaxis: {
    labels: {
      style: {
        colors: categories.map(() => dashboardPalette.slate),
        fontWeight: 600,
      },
    },
  },
  dataLabels: showDataLabels
    ? baseChartOptions.dataLabels
    : {
      enabled: false,
    },
  tooltip: {
    ...baseChartOptions.tooltip,
    y: {
      formatter: tooltipFormatter,
    },
  },
  colors: [color],
});

export const buildComparisonBarOptions = ({
  categories,
  yTitle,
  tooltipFormatter,
  columnWidth = '34%',
  borderRadius = 6,
  showDataLabels = true,
}) => ({
  ...baseChartOptions,
  chart: {
    ...baseChartOptions.chart,
    type: 'bar',
    height: 360,
    stacked: false,
  },
  plotOptions: {
    bar: {
      horizontal: false,
      columnWidth,
      borderRadius,
      endingShape: 'rounded',
      dataLabels: {
        position: 'top',
      },
    },
  },
  dataLabels: showDataLabels
    ? {
      ...baseChartOptions.dataLabels,
      offsetX: 0,
      offsetY: 0,
      style: {
        ...baseChartOptions.dataLabels.style,
        fontSize: '10px',
      },
    }
    : {
      enabled: false,
    },
  stroke: {
    show: false,
  },
  xaxis: {
    categories,
    labels: {
      style: {
        colors: categories.map(() => dashboardPalette.muted),
        fontWeight: 600,
      },
    },
    axisBorder: {
      show: false,
    },
    axisTicks: {
      show: false,
    },
  },
  yaxis: {
    title: {
      text: yTitle,
      style: {
        color: dashboardPalette.muted,
        fontWeight: 600,
      },
    },
    labels: {
      style: {
        colors: [dashboardPalette.muted],
      },
    },
  },
  fill: {
    opacity: 1,
  },
  tooltip: {
    ...baseChartOptions.tooltip,
    y: {
      formatter: tooltipFormatter,
    },
  },
  colors: [dashboardPalette.primary, dashboardPalette.secondary],
});

export const buildComparisonLineOptions = ({
  categories,
  yTitle,
  tooltipFormatter,
  colors = [dashboardPalette.primary, dashboardPalette.accent],
  dashArray = [0, 6],
  strokeWidth = [3, 3],
  tooltipEnabled = true,
  markerDiscrete = [],
  curve = 'smooth',
}) => ({
  ...baseChartOptions,
  chart: {
    ...baseChartOptions.chart,
    type: 'line',
    height: 360,
  },
  stroke: {
    curve,
    width: strokeWidth,
    dashArray,
  },
  dataLabels: {
    enabled: false,
  },
  markers: {
    size: 3,
    strokeWidth: 0,
    discrete: markerDiscrete,
    hover: {
      sizeOffset: 2,
    },
  },
  fill: {
    opacity: 0.95,
  },
  xaxis: {
    categories,
    labels: {
      style: {
        colors: categories.map(() => dashboardPalette.muted),
        fontWeight: 600,
      },
      rotate: -45,
      hideOverlappingLabels: false,
    },
    axisBorder: {
      show: false,
    },
    axisTicks: {
      show: false,
    },
  },
  yaxis: {
    title: {
      text: yTitle,
      style: {
        color: dashboardPalette.muted,
        fontWeight: 600,
      },
    },
    labels: {
      formatter: (value) => Number(value).toFixed(0),
      style: {
        colors: [dashboardPalette.muted],
      },
    },
  },
  legend: {
    ...baseChartOptions.legend,
    position: 'top',
  },
  tooltip: {
    ...baseChartOptions.tooltip,
    enabled: tooltipEnabled,
    y: {
      formatter: tooltipFormatter,
    },
  },
  colors,
});

export const buildDonutOptions = ({
  labels,
  series = [],
  colors,
  tooltipFormatter,
  totalLabel = 'Total',
}) => ({
  ...baseChartOptions,
  chart: {
    ...baseChartOptions.chart,
    type: 'donut',
    height: 320,
  },
  labels,
  colors,
  stroke: {
    width: 0,
  },
  plotOptions: {
    pie: {
      donut: {
        size: '64%',
        labels: {
          show: true,
          name: {
            show: true,
            color: dashboardPalette.muted,
          },
          value: {
            show: true,
            color: dashboardPalette.slate,
            fontWeight: 700,
            formatter: (value) => `${Math.round(Number(value) || 0)}`,
          },
          total: {
            show: true,
            label: totalLabel,
            color: dashboardPalette.muted,
            formatter: (w) => w.globals.seriesTotals
              .reduce((sum, value) => sum + value, 0),
          },
        },
      },
    },
  },
  dataLabels: {
    enabled: false,
  },
  legend: {
    ...baseChartOptions.legend,
    position: 'bottom',
    horizontalAlign: 'center',
    formatter: (seriesName, opts) => {
      const count = Number(series[opts.seriesIndex] || 0);
      const total = series.reduce((sum, value) => sum + Number(value || 0), 0);
      const percent = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';

      return `${seriesName}: ${count} (${percent}%)`;
    },
  },
  tooltip: {
    ...baseChartOptions.tooltip,
    y: {
      formatter: tooltipFormatter,
    },
  },
});
